import { Link } from '@tanstack/react-router';
import { ArrowLeft, LoaderCircle } from 'lucide-react';
import { useState, type SyntheticEvent } from 'react';

import { Button, Input, Label } from '@shigo-ui/ui';

import { dashboardApiErrorMessage } from '../../../lib/apiErrorMessages';
import { readShigoAppConfig } from '../../../lib/config';
import { dashboardT } from '../../../lib/messages';
import { requestPasswordReset } from '../api';
import { AuthPanel, AuthStep } from '../components/AuthLayout';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  async function handleSubmit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    const normalizedEmail = email.trim();
    if (!normalizedEmail) return;

    setError('');
    setSuccess(false);
    setIsSubmitting(true);

    try {
      await requestPasswordReset({ email: normalizedEmail }, readShigoAppConfig());
      setSuccess(true);
    } catch (requestError) {
      setError(dashboardApiErrorMessage(requestError));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AuthPanel
      description={dashboardT('auth.forgotPassword.description')}
      step={AuthStep.ForgotPassword}
      title={dashboardT('auth.forgotPassword.title')}
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        <div className="space-y-1.5">
          <Label className="text-muted-foreground text-xs" htmlFor="forgot-password-email">
            {dashboardT('auth.login.email')}
          </Label>
          <Input
            autoComplete="email"
            className="h-11 rounded-lg bg-transparent text-[15px]"
            id="forgot-password-email"
            name="email"
            onChange={(event) => {
              setEmail(event.currentTarget.value);
            }}
            placeholder={dashboardT('auth.forgotPassword.emailPlaceholder')}
            required
            type="email"
            value={email}
          />
        </div>
        {success ? (
          <p
            className="rounded-lg border border-emerald-600/20 bg-emerald-600/8 px-3 py-2.5 text-sm font-medium text-emerald-700 dark:text-emerald-400"
            role="status"
          >
            {dashboardT('auth.forgotPassword.success')}
          </p>
        ) : null}
        {error ? (
          <p
            className="text-destructive rounded-lg border border-current/20 bg-current/5 px-3 py-2.5 text-sm"
            role="alert"
          >
            {error}
          </p>
        ) : null}
        <Button className="mt-2 h-11 w-full" disabled={!email.trim() || isSubmitting} type="submit">
          {isSubmitting ? (
            <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : null}
          {isSubmitting
            ? dashboardT('auth.forgotPassword.submitting')
            : dashboardT('auth.forgotPassword.submit')}
        </Button>
        <Button asChild className="w-full" variant="ghost">
          <Link to="/login">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            {dashboardT('auth.forgotPassword.backToLogin')}
          </Link>
        </Button>
      </form>
    </AuthPanel>
  );
}
